import styled, { keyframes } from 'styled-components';
import { Input } from 'antd';

const { TextArea } = Input;

/* ── animations ─────────────────────────────────────────────── */

export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const blink = keyframes`
  0%, 80%, 100% { opacity: 0.2; transform: translateY(0); }
  40%           { opacity: 1;   transform: translateY(-3px); }
`;

/* ── page scaffold ──────────────────────────────────────────── */

export const Page = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  height: 100vh;
  box-sizing: border-box;
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

export const TitleBadge = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #0ecb81;
  background: linear-gradient(135deg, rgba(14, 203, 129, 0.22), rgba(14, 203, 129, 0.06));
  border: 1px solid rgba(14, 203, 129, 0.3);
`;

export const Title = styled.h1`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 26px;
  font-weight: 700;
  margin: 0;
  line-height: 1.1;
`;

export const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.md};
  margin: 4px 0 0 0;
  max-width: 620px;
  line-height: 1.5;
`;

export const HealthPill = styled.div<{ $state: 'online' | 'offline' | 'loading' }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid
    ${({ $state }) =>
      $state === 'online'
        ? 'rgba(14, 203, 129, 0.35)'
        : $state === 'offline'
        ? 'rgba(246, 70, 93, 0.35)'
        : '#2B3139'};
  background: ${({ $state }) =>
    $state === 'online'
      ? 'rgba(14, 203, 129, 0.12)'
      : $state === 'offline'
      ? 'rgba(246, 70, 93, 0.12)'
      : 'rgba(132, 142, 156, 0.1)'};
  color: ${({ $state }) =>
    $state === 'online' ? '#0ECB81' : $state === 'offline' ? '#F6465D' : '#848E9C'};
`;

export const Dot = styled.span<{ $state: 'online' | 'offline' | 'loading' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $state }) =>
    $state === 'online' ? '#0ECB81' : $state === 'offline' ? '#F6465D' : '#848E9C'};
  box-shadow: ${({ $state }) =>
    $state === 'online' ? '0 0 0 3px rgba(14, 203, 129, 0.18)' : 'none'};
`;

/* ── chat shell ─────────────────────────────────────────────── */

export const ChatShell = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 14px;
  overflow: hidden;
`;

export const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.xl};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.secondary};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.text.tertiary};
  }
`;

/* ── empty state ────────────────────────────────────────────── */

export const Empty = styled.div`
  margin: auto 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${({ theme }) => theme.spacing.lg};
`;

export const EmptyIcon = styled.div`
  font-size: 40px;
  color: #0ecb81;
  width: 76px;
  height: 76px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(14, 203, 129, 0.18), rgba(14, 203, 129, 0.04));
  border: 1px solid rgba(14, 203, 129, 0.25);
`;

export const EmptyTitle = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: 700;
`;

export const EmptyHint = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.md};
  max-width: 460px;
`;

export const SuggestionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  width: 100%;
  max-width: 760px;
`;

export const SuggestionCard = styled.button`
  text-align: left;
  background: ${({ theme }) => theme.colors.background.tertiary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 12px;
  padding: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.md};
  line-height: 1.5;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};

  &:hover:not(:disabled) {
    border-color: #0ecb81;
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .icon {
    color: #0ecb81;
    font-size: 18px;
  }
`;

/* ── message bubbles ────────────────────────────────────────── */

export const Row = styled.div<{ $user: boolean }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  flex-direction: ${({ $user }) => ($user ? 'row-reverse' : 'row')};
  align-items: flex-start;
  animation: ${fadeUp} 0.25s ease;
`;

export const Avatar = styled.div<{ $user: boolean }>`
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  background: ${({ $user }) =>
    $user ? 'rgba(14, 203, 129, 0.16)' : 'rgba(132, 142, 156, 0.14)'};
  color: ${({ $user }) => ($user ? '#0ECB81' : '#848E9C')};
`;

export const Bubble = styled.div<{ $user: boolean; $error?: boolean }>`
  max-width: 76%;
  padding: 12px 16px;
  border-radius: 14px;
  border-top-${({ $user }) => ($user ? 'right' : 'left')}-radius: 4px;
  font-size: ${({ theme }) => theme.fontSize.md};
  line-height: 1.6;
  color: ${({ $error }) => ($error ? '#F6465D' : '#EAECEF')};
  background: ${({ $user, $error }) =>
    $error
      ? 'rgba(246, 70, 93, 0.08)'
      : $user
      ? 'rgba(14, 203, 129, 0.12)'
      : '#0B0E11'};
  border: 1px solid
    ${({ $user, $error }) =>
      $error
        ? 'rgba(246, 70, 93, 0.4)'
        : $user
        ? 'rgba(14, 203, 129, 0.25)'
        : '#2B3139'};
  word-break: break-word;

  p {
    margin: 0 0 8px 0;
  }
  p:last-child {
    margin-bottom: 0;
  }
  ul {
    margin: 4px 0;
    padding-left: 20px;
  }
  code {
    background: rgba(132, 142, 156, 0.18);
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 13px;
    font-family: 'SFMono-Regular', Consolas, monospace;
  }
  strong {
    color: #fff;
    font-weight: 600;
  }
`;

/* ── typing indicator ───────────────────────────────────────── */

export const Typing = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 14px 16px;

  span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #0ecb81;
    animation: ${blink} 1.4s infinite ease-in-out both;
  }
  span:nth-child(2) {
    animation-delay: 0.2s;
  }
  span:nth-child(3) {
    animation-delay: 0.4s;
  }
`;

/* ── trace ──────────────────────────────────────────────────── */

export const TracePre = styled.pre`
  margin: 8px 0 0 0;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  padding: 10px;
  border-radius: 8px;
  overflow-x: auto;
  max-height: 280px;
`;

/* ── composer ───────────────────────────────────────────────── */

export const Composer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border.primary};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.background.secondary};
`;

export const ComposerInner = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 12px;
  padding: 6px 6px 6px 14px;
  transition: border-color 0.2s ease;

  &:focus-within {
    border-color: #0ecb81;
    box-shadow: 0 0 0 3px rgba(14, 203, 129, 0.12);
  }
`;

export const ChatInput = styled(TextArea)`
  &&& {
    background: transparent;
    border: none;
    box-shadow: none;
    color: ${({ theme }) => theme.colors.text.primary};
    padding: 8px 0;
    resize: none;
    font-size: ${({ theme }) => theme.fontSize.md};
  }
  &&&::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`;

export const SendButton = styled.button`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  color: #fff;
  background: #0ecb81;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #0db870;
  }
  &:disabled {
    background: ${({ theme }) => theme.colors.background.tertiary};
    color: ${({ theme }) => theme.colors.text.tertiary};
    cursor: not-allowed;
  }
`;

export const ComposerHint = styled.div`
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-top: 8px;
  text-align: center;
`;
